import { createContext, useContext, useEffect } from "react";
import { DateTime } from "luxon";
import { readFormFunnelSessionId } from "../formFunnel/utils/formFunnelSessionStorage";

export const EngagementContext = createContext<{ path: string } | null>(null);
// Timing is stored in our database, not Mixpanel.
const ENGAGEMENT_ENDPOINT = "/api/engagement";

type Properties = {
  scope: "page" | "step";
  stepId?: string;
  stepIndex?: number;
  phase?: string;
  fieldKeys?: string[];
  formFunnelId?: string;
  formFunnelSessionId?: string;
  landingPageId?: string;
  persistenceScope?: string;
  programId?: string;
  providerId?: string;
};

/** One measurement segment; cumulative checkpoints must be maxed, never summed. */
export function startEngagementTracking(path: string, { scope, ...context }: Properties) {
  const started = performance.now();
  const measurementId = crypto.randomUUID();
  const startedAt = DateTime.utc().toISO();
  let previous = started;
  let lastActivity = started;
  let visible = !document.hidden;
  let visibleMs = 0;
  let activeMs = 0;
  let firstInteractionMs: number | null = null;
  let sequence = 0;
  const account = () => {
    const now = performance.now();
    if (visible) {
      visibleMs += Math.max(0, now - previous);
      // Reading counts for up to 30s after entry, tab return, or an interaction.
      activeMs += Math.max(0, Math.min(now, lastActivity + 30_000) - previous);
    }
    previous = now;
    return now;
  };
  const checkpoint = (reason: string) => {
    const now = account();
    try {
      // A beacon survives navigation and page hide; the server keeps the highest sequence.
      navigator.sendBeacon(
        ENGAGEMENT_ENDPOINT,
        new Blob(
          [
            JSON.stringify({
              measurementId,
              scope,
              path,
              context,
              engagementVersion: 3,
              startedAt,
              observedAt: DateTime.utc().toISO(),
              sequence: sequence++,
              reason,
              elapsedMs: Math.round(now - started),
              visibleMs: Math.round(visibleMs),
              activeMs: Math.round(activeMs),
              firstInteractionMs,
            }),
          ],
          { type: "application/json" }
        )
      );
    } catch {
      // Collection is best effort and must never interrupt navigation or submission.
    }
  };
  const activity = () => {
    if (window.location.pathname !== path) return;
    const now = account();
    if (!visible) return;
    lastActivity = now;
    firstInteractionMs ??= Math.round(now - started);
  };
  const visibility = () => {
    if (document.hidden) checkpoint("hidden");
    else account();
    visible = !document.hidden;
    if (visible) lastActivity = performance.now();
  };
  const pagehide = () => {
    checkpoint("pagehide");
    visible = false;
  };
  const pageshow = () => {
    account();
    visible = !document.hidden;
    if (visible) lastActivity = performance.now();
  };
  const submit = () => {
    if (window.location.pathname === path) checkpoint("submit");
  };
  const navigation = (event: Event) => {
    if (
      window.location.pathname === path &&
      event.target instanceof Element &&
      event.target.closest("a[href], [data-navigation-action]")
    ) {
      checkpoint("navigation");
    }
  };
  const interactions = ["pointerdown", "keydown", "scroll", "input"] as const;
  interactions.forEach((name) =>
    document.addEventListener(name, activity, { capture: true, passive: true })
  );
  document.addEventListener("visibilitychange", visibility);
  document.addEventListener("submit", submit, true);
  document.addEventListener("click", navigation, true);
  window.addEventListener("pagehide", pagehide);
  window.addEventListener("pageshow", pageshow);
  // The start checkpoint is the page or step view.
  checkpoint("start");
  return () => {
    checkpoint("end");
    interactions.forEach((name) => document.removeEventListener(name, activity, true));
    document.removeEventListener("visibilitychange", visibility);
    document.removeEventListener("submit", submit, true);
    document.removeEventListener("click", navigation, true);
    window.removeEventListener("pagehide", pagehide);
    window.removeEventListener("pageshow", pageshow);
  };
}

/** Stable primitive metadata prevents timers restarting on ordinary React renders. */
export function useEngagementTracking(properties: Properties | null) {
  const context = useContext(EngagementContext);
  const serialized = JSON.stringify(properties);
  useEffect(() => {
    if (!context || serialized === "null") return;
    try {
      const properties: Properties = JSON.parse(serialized);
      if (
        properties.formFunnelId &&
        properties.formFunnelSessionId &&
        readFormFunnelSessionId(properties.formFunnelId, properties.persistenceScope) !==
          properties.formFunnelSessionId
      )
        return;
      return startEngagementTracking(context.path, properties);
    } catch {
      // Unsupported browser/storage must not prevent the user completing a form.
    }
  }, [context, serialized]);
}

export function PageEngagement() {
  useEngagementTracking({ scope: "page" });
  return null;
}
